import { useMutation, useQuery } from '@apollo/client/react';
import { Maximize2, SquarePen, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { OpenAssistantDetail } from '../../agent/assistant-dock';
import {
  ASSISTANT_PANEL_OPEN_KEY,
  OPEN_ASSISTANT_EVENT,
  takeDockAnnouncement,
} from '../../agent/assistant-dock';
import { ASSISTANT_SUGGESTIONS } from '../../agent/suggestions';
import { useAgentAttachments } from '../../agent/useAgentAttachments';
import { HOME_SESSION_KEY, useAgentChat } from '../../agent/useAgentChat';
import { useAuth } from '../../hooks/useAuth';
import { OnboardingTourAction } from '../../graphql/enums.graphql';
import { AnswerForControl } from '../../pages/Assistant/AnswerForControl';
import { OwnDocsToggle } from '../../pages/Assistant/OwnDocsToggle';
import { AssistantConversation } from '../../pages/Assistant/AssistantConversation';
import type {
  AdvanceOnboardingTourMutation,
  AdvanceOnboardingTourMutationVariables,
} from '../../pages/Onboarding/queries/advanceOnboardingTour.mutation.graphql';
import ADVANCE_ONBOARDING_TOUR from '../../pages/Onboarding/queries/advanceOnboardingTour.mutation.graphql';
import type { AssistantOnboardingQuery } from '../../pages/Onboarding/queries/assistantOnboarding.query.graphql';
import ASSISTANT_ONBOARDING from '../../pages/Onboarding/queries/assistantOnboarding.query.graphql';
import { withoutKickoff } from '../../pages/Onboarding/onboardingKickoff';
import { Brynn } from '../Brynn/Brynn';
import { Button } from '../Button/Button';
import styles from './AssistantWidget.module.css';
import type { TourBarAction } from './TourBar';
import { TourBar } from './TourBar';

const TOUR_ACTION: Record<Exclude<TourBarAction, 'finish'>, OnboardingTourAction> = {
  back: OnboardingTourAction.Back,
  skip: OnboardingTourAction.Skip,
  next: OnboardingTourAction.Next,
  pause: OnboardingTourAction.Pause,
  resume: OnboardingTourAction.Resume,
};

/** Open/closed state; the same key the previous bubble used, so it carries over. */
const OPEN_STORAGE_KEY = ASSISTANT_PANEL_OPEN_KEY;
/** Where the previous bubble kept its own conversation, before it shared the home one. */
const LEGACY_SESSION_KEY = 'revvly.agent.widget.session';

function readStored(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be unavailable (privacy mode): non-fatal.
  }
}

/**
 * The bubble used to keep its own conversation. It now shares the full-page
 * assistant's, so carry an existing bubble conversation over once, when the
 * full page has none of its own.
 */
function adoptLegacySession(): void {
  try {
    const legacy = localStorage.getItem(LEGACY_SESSION_KEY);
    if (legacy && !localStorage.getItem(HOME_SESSION_KEY)) {
      localStorage.setItem(HOME_SESSION_KEY, legacy);
    }
    localStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // non-fatal
  }
}

/**
 * The docked Revvly assistant: the bottom-right launcher and its panel. The
 * panel is the same conversation as the full-page assistant (one session, one
 * renderer, so every card, chip and confirmation works in both), and the page
 * behind it stays usable. Hidden on the assistant's own pages, which show the
 * conversation full size; coming back from them re-syncs the conversation.
 */
export function AssistantWidget() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hidden = !isAuthenticated || pathname === '/' || pathname.startsWith('/assistant');

  const [open, setOpen] = useState(() => readStored(OPEN_STORAGE_KEY) === 'true');
  const {
    messages,
    isStreaming,
    awaitingConfirmation,
    send,
    respondToAction,
    reviseProposal,
    restore,
    reset,
    syncFromStorage,
  } = useAgentChat();
  const attachmentController = useAgentAttachments();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  // A running onboarding tour, read from the server so it is right after a reload.
  const onboarding = useQuery<AssistantOnboardingQuery>(ASSISTANT_ONBOARDING, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });
  const tour = onboarding.data?.assistantOnboarding?.tour ?? null;
  const activeTour = tour && !tour.complete ? tour : null;
  const tourStep = activeTour?.steps.find((s) => s.key === activeTour.currentKey) ?? null;
  const [advanceTour, { loading: moving }] = useMutation<
    AdvanceOnboardingTourMutation,
    AdvanceOnboardingTourMutationVariables
  >(ADVANCE_ONBOARDING_TOUR);

  // The assistant may move the tour itself: re-read it when a reply ends.
  const { refetch: refetchOnboarding } = onboarding;
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming && isAuthenticated) void refetchOnboarding();
    wasStreaming.current = isStreaming;
  }, [isStreaming, isAuthenticated, refetchOnboarding]);

  // A page's own button (the ticket screen, the Knowledge page) opens the panel, sometimes with a
  // question ready in the box. The draft is one-shot: the conversation copies it into its box when
  // it changes (or when the panel mounts with it), and it is cleared straight after, so reopening the
  // panel later never brings an old question back, and the same question twice still lands.
  const [draft, setDraft] = useState<string | undefined>(undefined);
  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenAssistantDetail>).detail ?? {};
      setOpen(true);
      if (detail.draft) setDraft(detail.draft);
      else panelRef.current?.focus();
    };
    window.addEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, onOpen);
  }, []);
  // Runs after the conversation's own effects in the same commit, so it has taken the draft by now.
  useEffect(() => {
    if (draft !== undefined) setDraft(undefined);
  }, [draft]);

  // Arriving from the full-page onboarding: say so once, in the live region.
  const [announcement, setAnnouncement] = useState<string | null>(null);
  useEffect(() => {
    const pending = takeDockAnnouncement();
    if (pending) {
      setOpen(true);
      setAnnouncement(pending);
    }
  }, []);

  const moveTour = useCallback(
    async (action: TourBarAction) => {
      if (!activeTour) return;
      if (action === 'finish') {
        await send('Finish the tour.');
        return;
      }
      const result = await advanceTour({
        variables: { action: TOUR_ACTION[action], fromStep: activeTour.currentKey },
      });
      const after = result.data?.advanceOnboardingTour.tour;
      if (!after) return;
      // Write the new tour into the cached onboarding, so the bar moves at once.
      onboarding.updateQuery((prev) =>
        prev?.assistantOnboarding
          ? ({
              ...prev,
              assistantOnboarding: { ...prev.assistantOnboarding, tour: after },
            } as AssistantOnboardingQuery)
          : undefined,
      );
      if (action === 'pause') return;
      if (action === 'resume') {
        await send('I resumed the tour.');
        return;
      }
      if (after.currentKey === activeTour.currentKey) return;
      const step = after.steps.find((s) => s.key === after.currentKey);
      if (!step) return;
      navigate(step.route);
      setAnnouncement(`Step ${after.position} of ${after.count}: ${step.title}.`);
      await send(`I moved the tour to step ${after.position}: ${step.title}.`);
    },
    [activeTour, advanceTour, navigate, onboarding, send],
  );

  // Restore the shared conversation once, on the first authenticated mount.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || restoredRef.current) return;
    restoredRef.current = true;
    adoptLegacySession();
    void restore();
  }, [isAuthenticated, restore]);

  // The full page may have moved the conversation on (or started a new one)
  // while the bubble was hidden: pick that up when the bubble comes back.
  const wasHiddenRef = useRef(hidden);
  useEffect(() => {
    if (wasHiddenRef.current && !hidden && restoredRef.current) void syncFromStorage();
    wasHiddenRef.current = hidden;
  }, [hidden, syncFromStorage]);

  useEffect(() => {
    writeStored(OPEN_STORAGE_KEY, String(open));
  }, [open]);

  // Move focus into the panel when it opens, so keyboard users land in it.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    rootRef.current?.querySelector<HTMLButtonElement>('[data-assistant-launcher]')?.focus();
  }, []);

  // Escape closes the panel while focus is inside it.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && panelRef.current?.contains(document.activeElement)) {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (hidden) return null;

  return (
    <div ref={rootRef} className={styles.Root}>
      {open && (
        <section
          ref={panelRef}
          className={styles.Panel}
          role="dialog"
          aria-label="Revvly Assistant"
          tabIndex={-1}
        >
          <header className={styles.Header}>
            <div className={styles.Heading}>
              <h2 className={styles.Title}>{activeTour ? 'Your tour' : 'Revvly Assistant'}</h2>
              <p className={styles.Subtitle}>
                {activeTour
                  ? `Step ${activeTour.position} of ${activeTour.count} · ${tourStep?.title ?? ''}`
                  : 'Answers about your workspace and the docs'}
              </p>
            </div>
            <div className={styles.HeaderActions}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                forIconOnly
                aria-label="New chat"
                disabled={messages.length === 0 || isStreaming}
                onClick={reset}
              >
                <SquarePen aria-hidden="true" />
              </Button>
              <Link
                to="/assistant"
                className={styles.IconLink}
                aria-label="Open the full-page assistant"
                title="Open the full-page assistant"
              >
                <Maximize2 aria-hidden="true" />
              </Link>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                forIconOnly
                aria-label="Close the assistant"
                onClick={close}
              >
                <X aria-hidden="true" />
              </Button>
            </div>
          </header>
          {activeTour && (
            <TourBar
              tour={activeTour}
              disabled={moving || isStreaming || awaitingConfirmation}
              onAction={(action) => void moveTour(action)}
            />
          )}
          <AssistantConversation
            variant="panel"
            messages={withoutKickoff(messages)}
            isStreaming={isStreaming}
            send={send}
            respondToAction={respondToAction}
            reviseProposal={reviseProposal}
            suggestions={ASSISTANT_SUGGESTIONS}
            emptyTitle="Ask Revvly"
            emptySubtitle="Ask about your clients, revenue and invoices, or how anything in Revvly works."
            attachmentController={attachmentController}
            initialDraft={draft}
            composerAccessory={<AnswerForControl />}
            composerTools={<OwnDocsToggle />}
            copyableAnswers
          />
        </section>
      )}
      <p className={styles.Announce} role="status" aria-live="polite">
        {announcement}
      </p>
      <Button
        type="button"
        data-assistant-launcher
        className={styles.Launcher}
        aria-label={open ? 'Close the assistant' : 'Open the assistant'}
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Brynn className={styles.LauncherLogo} aria-hidden="true" />
      </Button>
    </div>
  );
}

export default AssistantWidget;
